import { useEffect, useRef, useImperativeHandle, forwardRef, useState } from 'react';
import { Renderer, Program, Mesh, Triangle, Vec3 } from 'ogl';
import './Orb.css';

// Smooth interpolation utility
const lerp = (start, end, factor) => start + (end - start) * factor;

const Orb = forwardRef(({ 
  initialHue = 0, 
  initialHoverIntensity = 0.2, 
  initialRotateOnHover = true, 
  initialForceHoverState = false 
}, ref) => {
  const ctnDom = useRef(null);
  
  // Target values that we animate towards
  const [targetValues, setTargetValues] = useState({
    hue: initialHue,
    hoverIntensity: initialHoverIntensity,
    rotateOnHover: initialRotateOnHover,
    forceHoverState: initialForceHoverState
  });
  
  // Current animated values
  const animatedValues = useRef({
    hue: initialHue,
    hoverIntensity: initialHoverIntensity,
    rotateOnHover: initialRotateOnHover ? 1 : 0,
    forceHoverState: initialForceHoverState ? 1 : 0
  });
  
  // Animation state
  const animationState = useRef({
    animating: false,
    startTime: 0,
    duration: 400 // 0.4 seconds default
  });

  // Expose control functions to parent components
  useImperativeHandle(ref, () => ({
    // Set hue with smooth transition (0-360 degrees)
    setHue: (newHue, duration = 400) => {
      animateToValue('hue', newHue, duration);
    },
    
    // Set hover intensity with smooth transition (0-1)
    setHoverIntensity: (intensity, duration = 400) => {
      animateToValue('hoverIntensity', Math.max(0, Math.min(1, intensity)), duration);
    },
    
    // Enable/disable rotation on hover with smooth transition
    setRotateOnHover: (enabled, duration = 400) => {
      animateToValue('rotateOnHover', enabled ? 1 : 0, duration);
      setTargetValues(prev => ({ ...prev, rotateOnHover: enabled }));
    },
    
    // Force hover state on/off with smooth transition
    setForceHoverState: (forced, duration = 400) => {
      animateToValue('forceHoverState', forced ? 1 : 0, duration);
      setTargetValues(prev => ({ ...prev, forceHoverState: forced }));
    },
    
    // Get current values
    getCurrentValues: () => ({
      hue: animatedValues.current.hue,
      hoverIntensity: animatedValues.current.hoverIntensity,
      rotateOnHover: animatedValues.current.rotateOnHover > 0.5,
      forceHoverState: animatedValues.current.forceHoverState > 0.5
    }),
    
    // Animate to multiple values simultaneously
    animateTo: (values, duration = 400) => {
      Object.keys(values).forEach(key => {
        if (key in animatedValues.current) {
          animateToValue(key, values[key], duration);
        }
      });
      setTargetValues(prev => ({ ...prev, ...values }));
    },
    
    // Reset to initial values
    reset: (duration = 400) => {
      animateToValue('hue', initialHue, duration);
      animateToValue('hoverIntensity', initialHoverIntensity, duration);
      animateToValue('rotateOnHover', initialRotateOnHover ? 1 : 0, duration);
      animateToValue('forceHoverState', initialForceHoverState ? 1 : 0, duration);
      setTargetValues({
        hue: initialHue,
        hoverIntensity: initialHoverIntensity,
        rotateOnHover: initialRotateOnHover,
        forceHoverState: initialForceHoverState
      });
    }
  }), [initialHue, initialHoverIntensity, initialRotateOnHover, initialForceHoverState]);

  // Animation function
  const animateToValue = (property, targetValue, duration = 400) => {
    const startValue = animatedValues.current[property];
    const startTime = performance.now();
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Very smooth easing function (ease-in-out cubic)
      const easedProgress = progress < 0.5 
        ? 4 * progress * progress * progress 
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      
      animatedValues.current[property] = lerp(startValue, targetValue, easedProgress);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  };

  const vert = /* glsl */ `
    precision highp float;
    attribute vec2 position;
    attribute vec2 uv;
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = vec4(position, 0.0, 1.0);
    }
  `;

  const frag = /* glsl */ `
    precision highp float;

    uniform float iTime;
    uniform vec3 iResolution;
    uniform float hue;
    uniform float hover;
    uniform float rot;
    uniform float hoverIntensity;
    varying vec2 vUv;

    vec3 rgb2yiq(vec3 c) {
      float y = dot(c, vec3(0.299, 0.587, 0.114));
      float i = dot(c, vec3(0.596, -0.274, -0.322));
      float q = dot(c, vec3(0.211, -0.523, 0.312));
      return vec3(y, i, q);
    }
    
    vec3 yiq2rgb(vec3 c) {
      float r = c.x + 0.956 * c.y + 0.621 * c.z;
      float g = c.x - 0.272 * c.y - 0.647 * c.z;
      float b = c.x - 1.106 * c.y + 1.703 * c.z;
      return vec3(r, g, b);
    }
    
    vec3 adjustHue(vec3 color, float hueDeg) {
      float hueRad = hueDeg * 3.14159265 / 180.0;
      vec3 yiq = rgb2yiq(color);
      float cosA = cos(hueRad);
      float sinA = sin(hueRad);
      float i = yiq.y * cosA - yiq.z * sinA;
      float q = yiq.y * sinA + yiq.z * cosA;
      yiq.y = i;
      yiq.z = q;
      return yiq2rgb(yiq);
    }

    vec3 hash33(vec3 p3) {
      p3 = fract(p3 * vec3(0.1031, 0.11369, 0.13787));
      p3 += dot(p3, p3.yxz + 19.19);
      return -1.0 + 2.0 * fract(vec3(
        p3.x + p3.y,
        p3.x + p3.z,
        p3.y + p3.z
      ) * p3.zyx);
    }

    float snoise3(vec3 p) {
      const float K1 = 0.333333333;
      const float K2 = 0.166666667;
      vec3 i = floor(p + (p.x + p.y + p.z) * K1);
      vec3 d0 = p - (i - (i.x + i.y + i.z) * K2);
      vec3 e = step(vec3(0.0), d0 - d0.yzx);
      vec3 i1 = e * (1.0 - e.zxy);
      vec3 i2 = 1.0 - e.zxy * (1.0 - e);
      vec3 d1 = d0 - (i1 - K2);
      vec3 d2 = d0 - (i2 - K1);
      vec3 d3 = d0 - 0.5;
      vec4 h = max(0.6 - vec4(
        dot(d0, d0),
        dot(d1, d1),
        dot(d2, d2),
        dot(d3, d3)
      ), 0.0);
      vec4 n = h * h * h * h * vec4(
        dot(d0, hash33(i)),
        dot(d1, hash33(i + i1)),
        dot(d2, hash33(i + i2)),
        dot(d3, hash33(i + 1.0))
      );
      return dot(vec4(31.316), n);
    }

    vec4 extractAlpha(vec3 colorIn) {
      float a = max(max(colorIn.r, colorIn.g), colorIn.b);
      return vec4(colorIn.rgb / (a + 1e-5), a);
    }

    const vec3 baseColor1 = vec3(0.611765, 0.262745, 0.996078);
    const vec3 baseColor2 = vec3(0.298039, 0.760784, 0.913725);
    const vec3 baseColor3 = vec3(0.062745, 0.078431, 0.600000);
    const float innerRadius = 0.5;
    const float noiseScale = 0.65;

    float light1(float intensity, float attenuation, float dist) {
      return intensity / (1.0 + dist * attenuation);
    }
    float light2(float intensity, float attenuation, float dist) {
      return intensity / (1.0 + dist * dist * attenuation);
    }

    vec4 draw(vec2 uv) {
      vec3 color1 = adjustHue(baseColor1, hue);
      vec3 color2 = adjustHue(baseColor2, hue);
      vec3 color3 = adjustHue(baseColor3, hue);
      
      float ang = atan(uv.y, uv.x);
      float len = length(uv);
      float invLen = len > 0.0 ? 1.0 / len : 0.0;
      
      float n0 = snoise3(vec3(uv * noiseScale, iTime * 0.5)) * 0.5 + 0.5;
      float r0 = mix(mix(innerRadius, 1.0, 0.4), mix(innerRadius, 1.0, 0.6), n0);
      float d0 = distance(uv, (r0 * invLen) * uv);
      float v0 = light1(1.0, 10.0, d0);
      v0 *= smoothstep(r0 * 1.05, r0, len);
      float cl = cos(ang + iTime * 2.0) * 0.5 + 0.5;
      
      float a = iTime * -1.0;
      vec2 pos = vec2(cos(a), sin(a)) * r0;
      float d = distance(uv, pos);
      float v1 = light2(1.5, 5.0, d);
      v1 *= light1(1.0, 50.0, d0);
      
      float v2 = smoothstep(1.0, mix(innerRadius, 1.0, n0 * 0.5), len);
      float v3 = smoothstep(innerRadius, mix(innerRadius, 1.0, 0.5), len);
      
      vec3 col = mix(color1, color2, cl);
      col = mix(color3, col, v0);
      col = (col + v1) * v2 * v3;
      
      // Add base fill for center area to reduce transparency
      float centerFill = 1.0 - smoothstep(0.0, innerRadius * 1.2, len);
      centerFill *= 0.3; // Adjust this value to control center opacity (0.0 = transparent, 1.0 = opaque)
      col = mix(col, color3 * 0.8, centerFill);
      
      col = clamp(col, 0.0, 1.0);
      
      return extractAlpha(col);
    }

    vec4 mainImage(vec2 fragCoord) {
      vec2 center = iResolution.xy * 0.5;
      float size = min(iResolution.x, iResolution.y);
      vec2 uv = (fragCoord - center) / size * 2.0;
      
      float angle = rot;
      float s = sin(angle);
      float c = cos(angle);
      uv = vec2(c * uv.x - s * uv.y, s * uv.x + c * uv.y);
      
      uv.x += hover * hoverIntensity * 0.1 * sin(uv.y * 10.0 + iTime);
      uv.y += hover * hoverIntensity * 0.1 * sin(uv.x * 10.0 + iTime);
      
      return draw(uv);
    }

    void main() {
      vec2 fragCoord = vUv * iResolution.xy;
      vec4 col = mainImage(fragCoord);
      gl_FragColor = vec4(col.rgb * col.a, col.a);
    }
  `;

  useEffect(() => {
    const container = ctnDom.current;
    if (!container) return;

    const renderer = new Renderer({ alpha: true, premultipliedAlpha: false });
    const gl = renderer.gl;
    gl.clearColor(0, 0, 0, 0);
    container.appendChild(gl.canvas);

    const geometry = new Triangle(gl);
    const program = new Program(gl, {
      vertex: vert,
      fragment: frag,
      uniforms: {
        iTime: { value: 0 },
        iResolution: {
          value: new Vec3(gl.canvas.width, gl.canvas.height, gl.canvas.width / gl.canvas.height)
        },
        hue: { value: animatedValues.current.hue },
        hover: { value: 0 },
        rot: { value: 0 },
        hoverIntensity: { value: animatedValues.current.hoverIntensity }
      }
    });

    const mesh = new Mesh(gl, { geometry, program });

    function resize() {
      if (!container) return;
      const dpr = window.devicePixelRatio || 1;
      const width = container.clientWidth;
      const height = container.clientHeight;
      renderer.setSize(width * dpr, height * dpr);
      gl.canvas.style.width = width + 'px';
      gl.canvas.style.height = height + 'px';
      program.uniforms.iResolution.value.set(gl.canvas.width, gl.canvas.height, gl.canvas.width / gl.canvas.height);
    }
    window.addEventListener('resize', resize);
    resize();

    let targetHover = 0;
    let lastTime = 0;
    let currentRot = 0;
    const rotationSpeed = 0.4;

    const handleMouseMove = e => {
      // Hover effect disabled - keep targetHover at 0
      targetHover = 0;
    };

    const handleMouseLeave = () => {
      targetHover = 0;
    };

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);

    let rafId;
    const update = t => {
      rafId = requestAnimationFrame(update);
      const dt = (t - lastTime) * 0.001;
      lastTime = t;
      program.uniforms.iTime.value = t * 0.001;
      
      // Use animated values
      program.uniforms.hue.value = animatedValues.current.hue;
      program.uniforms.hoverIntensity.value = animatedValues.current.hoverIntensity;

      const effectiveHover = animatedValues.current.forceHoverState > 0.5 ? 1 : targetHover;
      program.uniforms.hover.value += (effectiveHover - program.uniforms.hover.value) * 0.1;

      if (animatedValues.current.rotateOnHover > 0.5 && effectiveHover > 0.5) {
        currentRot += dt * rotationSpeed;
      }
      program.uniforms.rot.value = currentRot;

      renderer.render({ scene: mesh });
    };
    rafId = requestAnimationFrame(update);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeChild(gl.canvas);
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, []); // Empty dependency array since we use refs for animated values

  return <div ref={ctnDom} className="orb-container" />;
});

Orb.displayName = 'Orb';

export default Orb;
